import React from 'react';
import { css } from '@emotion/react';
import { colors, fontStyles, spacing } from '../../../styles';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export class OnboardingErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Onboarding error boundary caught an error:', error, errorInfo);

    // Track error for debugging purposes
    if (typeof window !== 'undefined' && window.posthog) {
      window.posthog.capture('Onboarding Error Boundary Triggered', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: Date.now(),
      });
    }
  }

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: ${spacing.deci};
            text-align: center;
            min-height: 200px;
          `}
        >
          <div
            css={css`
              ${fontStyles.dog};
              color: ${colors.silver};
              margin-bottom: ${spacing.centi};
            `}
          >
            Something went wrong with this step
          </div>
          <button
            onClick={() => this.setState({ hasError: false, error: undefined })}
            css={css`
              background: ${colors.petrolBlue};
              color: ${colors.sugar};
              border: none;
              border-radius: 8px;
              padding: ${spacing.milli} ${spacing.centi};
              cursor: pointer;
              ${fontStyles.cat};

              &:hover {
                background: ${colors.skyBlue};
              }
            `}
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
